import { AlertCircle, ChevronDown } from "lucide-react";

import { strings } from "@perceptron/constants";
import { cn } from "@perceptron/utils/cn";

import type { TrackingInspectorRow } from "./status";
import { formatConfidence } from "./tools";
import { formatSeconds } from "../../utils/time";

const copy = strings.trackingInspector;
const summaryClassName =
  "flex cursor-pointer list-none items-center justify-between gap-2 rounded-md py-2 text-xs font-medium text-foreground-secondary transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring [&::-webkit-details-marker]:hidden";
const definitionClassName = "grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-xs";

export function TrackDiagnostics({
  row,
  currentTime,
  source,
  showReason,
  hasChosenConfidence,
  hasSource,
  navigationHint,
}: {
  row: TrackingInspectorRow;
  currentTime: number;
  source: string;
  showReason: boolean;
  hasChosenConfidence: boolean;
  hasSource: boolean;
  navigationHint: { id: string; text: string };
}) {
  return (
    <div className="divide-y divide-border border-t border-border">
      <details className="group">
        <summary className={summaryClassName}>
          {copy.resultDetails}
          <ChevronDown
            aria-hidden="true"
            className="size-3.5 shrink-0 transition-transform group-open:rotate-180 motion-reduce:transition-none"
          />
        </summary>
        <div className="space-y-3 pb-3 pt-1">
          {!showReason && (
            <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-foreground-secondary">
              {row.detail}
            </p>
          )}
          {!!row.frameTimes?.length && (
            <p className="text-xs leading-relaxed text-foreground-secondary" id={navigationHint.id}>
              {navigationHint.text}
            </p>
          )}
          <dl className={definitionClassName}>
            <dt className="text-foreground-secondary">{copy.status}</dt>
            <dd>{row.title}</dd>
            {row.recovery?.outcome && (
              <>
                <dt className="text-foreground-secondary">{copy.recovery.response}</dt>
                <dd>
                  {row.recovery.outcome === "found"
                    ? copy.recovery.withBox
                    : row.recovery.outcome === "no_match"
                      ? copy.recovery.noMatch
                      : copy.recovery.stages.failed}
                </dd>
              </>
            )}
            {row.recovery && (
              <>
                <dt className="text-foreground-secondary">{copy.recovery.status}</dt>
                <dd>
                  {copy.recovery.stages[row.recovery.status]}
                  {row.recovery.targetTime !== undefined && (
                    <span className="ml-1 tabular-nums text-foreground-secondary">
                      · {formatSeconds(row.recovery.targetTime)}
                    </span>
                  )}
                </dd>
              </>
            )}
            {row.frame?.recovery && (
              <>
                <dt className="text-foreground-secondary">{copy.recovery.anchor}</dt>
                <dd className="tabular-nums">
                  {formatSeconds(row.frame.recovery.anchorTime)}
                  <span className="ml-1 text-foreground-secondary">· {copy.recovery.assisted}</span>
                </dd>
              </>
            )}
          </dl>
        </div>
      </details>

      {row.warnings.length > 0 && (
        <details className="group">
          <summary className={summaryClassName}>
            <span className="flex items-center gap-1.5">
              <AlertCircle aria-hidden="true" className="size-3.5" />
              {copy.warnings}
              <span className="tabular-nums">{row.warnings.length}</span>
            </span>
            <ChevronDown
              aria-hidden="true"
              className="size-3.5 shrink-0 transition-transform group-open:rotate-180 motion-reduce:transition-none"
            />
          </summary>
          <ul className="space-y-2 pb-3 pt-1 text-xs leading-relaxed text-foreground-secondary">
            {row.warnings.map((warning, index) => (
              <li className="whitespace-pre-wrap break-words" key={`${index}:${warning}`}>
                {warning}
              </li>
            ))}
          </ul>
        </details>
      )}

      <details className="group">
        <summary className={summaryClassName}>
          {copy.technicalDetails}
          <ChevronDown
            aria-hidden="true"
            className="size-3.5 shrink-0 transition-transform group-open:rotate-180 motion-reduce:transition-none"
          />
        </summary>
        <div className="space-y-3 pb-1 pt-1">
          <dl className={definitionClassName}>
            <dt className="text-foreground-secondary">{copy.trackId}</dt>
            <dd className="break-all font-mono">{row.trackId}</dd>
            <dt className="text-foreground-secondary">{copy.currentFrame}</dt>
            <dd className="tabular-nums">{formatSeconds(row.frame?.timestamp ?? currentTime)}</dd>
            {!hasSource && (
              <>
                <dt className="text-foreground-secondary">{copy.source}</dt>
                <dd>{source}</dd>
              </>
            )}
            {row.workerId !== undefined && (
              <>
                <dt className="text-foreground-secondary">{copy.worker}</dt>
                <dd className="tabular-nums">{row.workerId + 1}</dd>
              </>
            )}
          </dl>
          {(hasChosenConfidence ||
            row.frame?.sparse ||
            row.frame?.nano ||
            row.recovery?.timings ||
            row.recovery?.usage) && (
            <dl className={definitionClassName}>
              {hasChosenConfidence && row.frame && (
                <>
                  <dt className="text-foreground-secondary">{copy.confidence}</dt>
                  <dd className="text-right tabular-nums">{formatConfidence(row.frame.confidence)}</dd>
                </>
              )}
              {row.frame?.sparse && (
                <>
                  <dt className="text-foreground-secondary">{copy.sparseConfidence}</dt>
                  <dd className="text-right tabular-nums">{formatConfidence(row.frame.sparse.confidence)}</dd>
                </>
              )}
              {row.frame?.nano && (
                <>
                  <dt className="text-foreground-secondary">{copy.nanoConfidence}</dt>
                  <dd className="text-right tabular-nums">{formatConfidence(row.frame.nano.confidence)}</dd>
                </>
              )}
              {row.recovery?.timings && (
                <>
                  <dt className="text-foreground-secondary">{copy.recovery.requestTime}</dt>
                  <dd className="text-right tabular-nums">{row.recovery.timings.totalMs.toFixed(1)} ms</dd>
                </>
              )}
              {row.recovery?.usage && (
                <>
                  <dt className="text-foreground-secondary">{copy.recovery.tokens}</dt>
                  <dd className="text-right tabular-nums">{row.recovery.usage.totalTokens}</dd>
                </>
              )}
            </dl>
          )}
          {row.timings && (
            <div>
              <h5 className="mb-2 text-xs font-medium">{copy.timings}</h5>
              <dl className={cn(definitionClassName, "text-foreground-secondary")}>
                {(
                  [
                    [copy.decodeTime, row.timings.decodeMs],
                    [copy.sparseTime, row.timings.sparseMs],
                    [copy.nanoTime, row.timings.nanoMs],
                    [copy.nanoInitializationTime, row.timings.nanoInitializationMs],
                    [copy.totalTime, row.timings.totalMs],
                  ] as const
                ).map(([label, value]) => (
                  <div className="contents" key={label}>
                    <dt>{label}</dt>
                    <dd className="text-right tabular-nums">{value.toFixed(1)} ms</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </details>
    </div>
  );
}
